import {
  CodatClientInvitation,
  useInviteUserToClient,
} from "api/clientInvites";
import {
  ClientLoginSetting,
  CodatUser,
  useClientLoginQuery,
  useClientLoginSettingMutation,
  useEditUserMutation,
} from "api/users";
import { AxiosPromise, AxiosRequestConfig } from "axios";
import { createContext, useContext } from "react";

export interface InviteUserService {
  isLoading: () => boolean;
  inviteUser: (
    email: string,
    clientRole: string
  ) => AxiosPromise<CodatClientInvitation>;
}

export interface EditUserService {
  isLoading: () => boolean;
  editUser: (user: CodatUser) => AxiosPromise<CodatUser>;
}

export interface GetLoginSettingsService {
  isLoading: () => boolean;
  get: (options: AxiosRequestConfig) => AxiosPromise<ClientLoginSetting[]>;
}

export interface SetLoginSettingService {
  isLoading: () => boolean;
  setLoginSetting: (loginSetting: {
    typeId: number;
    enabled: boolean;
  }) => AxiosPromise<unknown>;
}

export interface UserManagementService {
  inviteUser: InviteUserService;
  editUser: EditUserService;
  getLoginSettings: GetLoginSettingsService;
  setLoginSetting: SetLoginSettingService;
}

export const UserManagementServiceContext = createContext<
  UserManagementService | undefined
>(undefined);

export const CreateUserManagementService = (): UserManagementService => {
  const [{ loading: inviteUserLoading }, inviteUserToClientApi] =
    useInviteUserToClient();

  const inviteUser: InviteUserService = {
    isLoading: () => inviteUserLoading,
    inviteUser: (email: string, clientRole: string) =>
      inviteUserToClientApi({ data: { email, clientRole } }),
  };

  const [{ loading: editUserLoading }, updateUserApi] = useEditUserMutation();

  const editUser: EditUserService = {
    isLoading: () => editUserLoading,
    editUser: (user: CodatUser) => updateUserApi({ data: user }),
  };

  const [{ loading: getLoginSettingsLoading }, invokeGetLoginSettings] =
    useClientLoginQuery();

  const [{ loading: setLoginSettingLoading }, setLoginSettingApi] =
    useClientLoginSettingMutation();

  const getLoginSettings: GetLoginSettingsService = {
    isLoading: () => getLoginSettingsLoading,
    get: (options: AxiosRequestConfig) => invokeGetLoginSettings(options),
  };

  const setLoginSetting: SetLoginSettingService = {
    isLoading: () => setLoginSettingLoading,
    setLoginSetting: (loginSetting: { typeId: number; enabled: boolean }) =>
      setLoginSettingApi({ data: loginSetting }),
  };

  return { inviteUser, editUser, getLoginSettings, setLoginSetting };
};

export const useUserManagementService = () =>
  useContext(UserManagementServiceContext);
